import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { Plus } from 'lucide-react';

import {
  useAdminCcRequests,
  AdminCcRequest,
  CcRequestOrderBy,
} from 'hooks/swrRequests/useAdminCcRequests';
import { useAdminCcMatches, CcMatchOrderBy } from 'hooks/swrRequests/useAdminCcMatches';
import { useAdminCcMetrics } from 'hooks/swrRequests/useAdminCcMetrics';
import type { Artist } from 'types';

import type { Scope, SortDirection } from './types';
import LogRequestModal, { LogRequestInitialValue } from './log-request-modal';
import LogMatchModal from './log-match-modal';
import RequestsTable from './requests-table';
import MatchesTable from './matches-table';
import StatsRow from './components/stats-row';
import FilterRow from './components/filter-row';
import PaginationBar from 'components/admin/pagination-bar';
import PrimaryButton from './components/primary-button';
import SegmentedTabs, { SegmentedTab } from 'components/admin/segmented-tabs';

const PAGE_LIMIT = 10;
type TabKey = 'requests' | 'matches';

const CollabClub: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('requests');
  const [scope, setScope] = useState<Scope>('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState<CcRequestOrderBy>('requestDate');
  const [orderDirection, setOrderDirection] = useState<SortDirection>('DESC');
  const [page, setPage] = useState(1);
  const [logOpen, setLogOpen] = useState(false);
  const [logMatchOpen, setLogMatchOpen] = useState(false);
  // When set, the Log Manually dialog opens prefilled (from a per-pair Connect).
  const [logInitial, setLogInitial] = useState<LogRequestInitialValue | null>(null);

  // Matches tab has its own scope / search / sort / pagination.
  const [matchScope, setMatchScope] = useState<Scope>('all');
  const [matchSearchInput, setMatchSearchInput] = useState('');
  const [matchSearch, setMatchSearch] = useState('');
  const [matchOrderBy, setMatchOrderBy] = useState<CcMatchOrderBy>('matchDate');
  const [matchOrderDirection, setMatchOrderDirection] = useState<SortDirection>('DESC');
  const [matchPage, setMatchPage] = useState(1);

  // Debounce each search input into the value used for fetching.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput), 350);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    const handle = setTimeout(() => setMatchSearch(matchSearchInput), 350);
    return () => clearTimeout(handle);
  }, [matchSearchInput]);

  // Any change to scope / search / sort resets to the first page.
  useEffect(() => {
    setPage(1);
  }, [scope, search, orderBy, orderDirection]);

  useEffect(() => {
    setMatchPage(1);
  }, [matchScope, matchSearch, matchOrderBy, matchOrderDirection]);

  const { requests, pagination, totalItems, isLoading, error, refetch } = useAdminCcRequests({
    scope,
    search,
    orderBy,
    orderDirection,
    page,
    limit: PAGE_LIMIT,
  });

  // Both hooks run regardless of the active tab so both count chips stay populated.
  const {
    matches,
    pagination: matchPagination,
    totalItems: matchesTotal,
    isLoading: matchesLoading,
    error: matchesError,
    refetch: refetchMatches,
  } = useAdminCcMatches({
    scope: matchScope,
    search: matchSearch,
    orderBy: matchOrderBy,
    orderDirection: matchOrderDirection,
    page: matchPage,
    limit: PAGE_LIMIT,
  });

  // All-time totals for the dashboard stat cards (unscoped, no pagination).
  const { metrics, refetch: refetchMetrics } = useAdminCcMetrics();

  const handleSort = (column?: CcRequestOrderBy) => {
    if (!column) return;
    if (orderBy === column) {
      setOrderDirection((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setOrderBy(column);
      setOrderDirection('ASC');
    }
  };

  const handleMatchSort = (column: CcMatchOrderBy) => {
    if (matchOrderBy === column) {
      setMatchOrderDirection((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setMatchOrderBy(column);
      setMatchOrderDirection('ASC');
    }
  };

  // The toolbar action follows the active tab: log a request on Requests, log a match on
  // Matches.
  const handleLogClick = () => {
    if (tab === 'matches') {
      setLogMatchOpen(true);
    } else {
      setLogInitial(null);
      setLogOpen(true);
    }
  };

  // Per-pair Connect: open the Log Manually dialog prefilled with the REVERSE connection —
  // the request already has requester → partner, so we log the reciprocal partner → requester,
  // which auto-matches the pair into a connection.
  const handleConnect = (request: AdminCcRequest, partner: Artist) => {
    if (!request.fromCcArtist) return;
    setLogInitial({
      fromArtist: partner,
      wants: [request.fromCcArtist],
      date: request.requestDate ?? undefined,
    });
    setLogOpen(true);
  };

  const tabs: SegmentedTab<TabKey>[] = useMemo(
    () => [
      { key: 'requests', label: 'Requests', count: totalItems },
      { key: 'matches', label: 'Matches', count: matchesTotal },
    ],
    [totalItems, matchesTotal]
  );

  const totalPages = pagination?.totalPages ?? 1;
  const matchTotalPages = matchPagination?.totalPages ?? 1;

  return (
    <Box
      padding={{ xs: 2, md: 4 }}
      maxWidth={1280}
      marginX="auto"
      // Make MUI table cells / inputs / avatar initials use the same font as the
      // rest of the admin (Founders Grotesk) instead of MUI's default typography.
      sx={{
        '& .MuiTableCell-root': { fontFamily: 'inherit' },
        '& .MuiInputBase-input': { fontFamily: 'inherit' },
        '& .MuiAvatar-root': { fontFamily: 'inherit' },
        '& .MuiTableRow-root.MuiTableRow-hover:hover': { backgroundColor: '#FAFBFC' },
      }}
    >
      {/* Dashboard statistics */}
      <StatsRow metrics={metrics} />

      {/* Requests / Matches section */}
      <Box border="1px solid #e2e8f0" borderRadius="8px" overflow="hidden">
        {/* Section head: segmented control */}
        <Box
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          gap={1.5}
          padding="16px 20px"
          borderBottom="1px solid #e2e8f0"
        >
          <SegmentedTabs tabs={tabs} active={tab} onChange={setTab} />

          <PrimaryButton
            variant="contained"
            size="small"
            onClick={handleLogClick}
            startIcon={<Plus size={16} />}
            sx={{ fontSize: 16 }}
          >
            {tab === 'matches' ? 'Log match' : 'Log request'}
          </PrimaryButton>
        </Box>

        {tab === 'requests' ? (
          <>
            <FilterRow
              scope={scope}
              onScopeChange={setScope}
              searchInput={searchInput}
              onSearchInputChange={setSearchInput}
            />

            <RequestsTable
              requests={requests}
              isLoading={isLoading}
              error={error}
              search={search}
              orderBy={orderBy}
              orderDirection={orderDirection}
              onSort={handleSort}
              onClearSearch={() => setSearchInput('')}
              onConnect={handleConnect}
            />

            <PaginationBar page={page} totalPages={totalPages} onPageChange={setPage} />
          </>
        ) : (
          <>
            <FilterRow
              scope={matchScope}
              onScopeChange={setMatchScope}
              searchInput={matchSearchInput}
              onSearchInputChange={setMatchSearchInput}
            />

            <MatchesTable
              matches={matches}
              isLoading={matchesLoading}
              error={matchesError}
              search={matchSearch}
              orderBy={matchOrderBy}
              orderDirection={matchOrderDirection}
              onSort={handleMatchSort}
              onClearSearch={() => setMatchSearchInput('')}
            />
            <PaginationBar
              page={matchPage}
              totalPages={matchTotalPages}
              onPageChange={setMatchPage}
            />
          </>
        )}
      </Box>

      <LogRequestModal
        open={logOpen}
        initialValue={logInitial}
        onClose={() => setLogOpen(false)}
        onCreated={() => {
          setLogOpen(false);
          // A new request can auto-match a reciprocal pair, so refresh matches
          // and the stat-card totals alongside the requests list.
          refetch();
          refetchMatches();
          refetchMetrics();
        }}
      />

      <LogMatchModal
        open={logMatchOpen}
        onClose={() => setLogMatchOpen(false)}
        onCreated={() => {
          setLogMatchOpen(false);
          // A direct match changes the matches list and the stat-card totals.
          refetchMatches();
          refetchMetrics();
          refetch();
        }}
      />
    </Box>
  );
};

export default CollabClub;
